import { QueryBuilder } from "@cgarciagarcia/react-query-builder";

import { IntakeFormStatus, Role, Source, User } from "~/shared.types";
import type { PaginatedServiceResponse, ServiceResponse } from "./api.types";
import { api } from "./axios";

export const getUsers = async ({
  queryBuilder,
}: {
  queryBuilder?: QueryBuilder<Record<string, string> | undefined>;
}) => {
  const response = await api.get<PaginatedServiceResponse<User[]>>(
    `/admin/users${queryBuilder?.build()}`,
  );

  return response.data;
};

interface CreateUserData {
  firstName: string;
  lastName: string;
  email: string;
}

export const createUser = async (data: CreateUserData) => {
  const response = await api.post<ServiceResponse<User>>("admin/users", data);

  return response.data;
};

export const getUser = async (id: User["id"]) => {
  const response = await api.get<ServiceResponse<User>>(`admin/users/${id}`);

  return response.data;
};

interface ChangeRoleData {
  id: User["id"];
  role: Role;
}

export const changeRole = async ({ id, role }: ChangeRoleData) => {
  const response = await api.patch<ServiceResponse<User>>(
    `/admin/users/${id}/role`,
    {
      role,
    },
  );

  return response.data;
};

interface CanLoginResponse {
  canLogin: boolean;
  reason?: string;
  hash?: string;
}

export const canLogin = async (email: string) => {
  const response = await api.get<ServiceResponse<CanLoginResponse>>(
    `/user/can-login?email=${email}`,
  );

  return response.data.data;
};

export const getUserByEmail = async (email: string) => {
  const response = await api.get<ServiceResponse<User>>(
    `/admin/users/by-email?email=${email}`,
  );

  return response.data.data;
};

export const getCurrentUser = async () => {
  const response = await api.get<ServiceResponse<User>>("/user");

  return response.data.data;
};

interface UpdateUserData {
  firstName: string;
  lastName: string;
}

export const updateUser = async (data: UpdateUserData) => {
  const response = await api.put<ServiceResponse<User>>("/user", data);

  return response.data;
};

export const checkIntakeFormStatus = async (userId: string) => {
  const response = await api.get<ServiceResponse<{ status: IntakeFormStatus }>>(
    `/users/${userId}/intake-form/status`,
  );

  return response.data.data.status;
};

export interface UserAppointmentProvider {
  id: string | number;
  name: string;
  title: string;
  description: string | null;
  image: string | null;
}

export interface UserAppointment {
  id: string;
  appointment_type: {
    slug: string;
    duration: number;
  };
  date: string;
  provider: UserAppointmentProvider | null;
  status: string;
  join_link: string | null;
  timezone: string;
}

interface GetUserAppointmentsParams {
  isUpcoming?: boolean;
}

export const getUserAppointments = async ({
  isUpcoming,
}: GetUserAppointmentsParams = {}) => {
  const response = await api.get<ServiceResponse<UserAppointment[]>>(
    "/user/appointments",
    { params: { is_upcoming: isUpcoming } },
  );

  return response.data.data;
};

export interface CancelUserAppointmentData {
  appointment_cancel_reason_id: number;
  cancellation_notes?: string;
}

export const cancelUserAppointment = async (
  appointmentId: UserAppointment["id"],
  data: CancelUserAppointmentData,
) => {
  await api.post<void>(`/appointments/${appointmentId}/cancel`, data);
};

interface UpdateUserFromAdminData {
  isTest?: boolean;
  source?: Source;
}

export const sendMagicLink = async (userId: User["id"]) => {
  const response = await api.post<ServiceResponse<{ message: string }>>(
    `admin/users/${userId}/magic-link`,
  );
  return response.data;
};

export const updateUserFromAdmin = async (
  userId: User["id"],
  data: UpdateUserFromAdminData,
) => {
  const payload = Object.entries(data).reduce(
    (acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>,
  );

  const response = await api.patch<ServiceResponse<User>>(
    `admin/users/${userId}`,
    payload,
  );
  return response.data;
};

export const updateUserEmail = async (userId: User["id"], email: string) => {
  const response = await api.patch<ServiceResponse<User>>(
    `admin/users/${userId}/email`,
    { email },
  );
  return response.data;
};
